// Status effects (buffs and debuffs). See docs/05-combat-sim §5.11.2 / §5.12.
// 59-effect master:
//   - 42 base effects (7 elements × 6 effects: stacking-debuff -> severe /
//     single-debuff / stacking-buff -> upper / single-buff layout)
//   - 7 element marks (mark.<element>, ADR 0013, 軸 1 属性スタッキング用)
//   - 1 kill_streak (ADR 0013, 軸 9 キル連鎖用、max 7 stack)
//   - 9 stat-boost.<type> (ADR 0015, 軸 6 archetype 2/7/8 の素 stat passive 用、
//     `Effect.applyBuff` 経路で表現。`STATUS_EFFECT_DEFS` は本 PR では埋めない
//     (Partial 設計、sim 側 stat 反映ロジックは Phase 4 別 ADR スコープ))
/**
 * All 59 status effect IDs:
 *   - 42 base (7 elements × 6 effects)
 *   - 7 element marks (`mark.<element>`, ADR 0013)
 *   - 1 kill_streak (ADR 0013)
 *   - 9 stat-boost.<type> (ADR 0015、軸 6 archetype 2/7/8 用、`Effect.applyBuff` 経路で表現)
 */
export const STATUS_EFFECTS = [
    // physical
    'bleed',
    'armor_break',
    'crush',
    'fighting_spirit',
    'mighty',
    'stance',
    // fire
    'burn',
    'scald',
    'parch',
    'heat_aura',
    'fire_circle',
    'ignite',
    // ice
    'chill',
    'freeze',
    'frostbind',
    'ice_film',
    'ice_wall',
    'composure',
    // lightning
    'shock',
    'paralyze',
    'overload',
    'charge',
    'overcharge',
    'flash',
    // earth
    'pressure',
    'petrify',
    'brittle',
    'harden',
    'earth_guard',
    'shelter',
    // light
    'dazzle',
    'stigma',
    'seal',
    'blessing',
    'divine_grace',
    'cleanse',
    // dark
    'poison',
    'erosion',
    'curse',
    'absorb',
    'blood_oath',
    'shadow_veil',
    // ADR 0013: 7 element marks for 軸 1 (属性スタッキング)
    'mark.physical',
    'mark.fire',
    'mark.ice',
    'mark.lightning',
    'mark.earth',
    'mark.light',
    'mark.dark',
    // ADR 0013: kill_streak for 軸 9 (キル連鎖)
    'kill_streak',
    // ADR 0015: 軸 6 archetype 2/7/8 stat-boost passive (catalogue 投入で `Effect.applyBuff` 経路に必要)
    //   - archetype 2 (素 stat passive 6): atk-bonus / crit-rate / crit-dmg / armor / hp-max / status-resist
    //   - archetype 7 (移動速度 1): move-speed
    //   - archetype 8 (命中・回避 2): accuracy / dodge
    // sim 側の stat 反映 reducer は Phase 4 別 ADR スコープ。本 PR は型レベル投入のみ
    // (`STATUS_EFFECT_DEFS` も埋めない、ADR 0014 §2.8 Partial 設計を継承)。
    'stat-boost.atk-bonus',
    'stat-boost.crit-rate',
    'stat-boost.crit-dmg',
    'stat-boost.armor',
    'stat-boost.hp-max',
    'stat-boost.status-resist',
    'stat-boost.move-speed',
    'stat-boost.accuracy',
    'stat-boost.dodge',
];
/**
 * Sentinel `expiresAt` for buffs that persist for the whole battle and are
 * cleared only when `state.entities` is dropped at battle end (no
 * `durationTicks`). `kill_streak` (軸 9) is the sole user today. UI layers
 * detect this value to render a permanence marker instead of a bogus
 * countdown (`(expiresAt - tick) / TICKS_PER_SECOND` would otherwise show a
 * ~300-trillion-second remainder).
 */
export const PERMANENT_BUFF_EXPIRES_AT = Number.MAX_SAFE_INTEGER;
/**
 * Status effect definition registry. ADR 0014 §2.8 / 軸 1 §G / 軸 9 §6.1。
 *
 * 本 ADR (0014) では **`mark.<element>` 7 種 + `kill_streak` 1 種 = 計 8 件のみ**
 * 実体定義する (mark stack reducer / kill_streak reducer が参照する固定値)。
 * 既存 42 種の `StatusEffectDef` 全埋めは ADR 0015 (catalogue 投入) のスコープ
 * のため、`Partial<Record<>>` で部分定義として表現する。
 *
 * mark.* の `durationTicks` は属性別の `decayTicks` (1 stack あたりの自然減衰時間、
 * 軸 1 §G で属性別差別化値 pin 済)。`kill_streak` は戦闘終了で消滅するため
 * `durationTicks: 0` でダミー値 (sim 側 reducer は戦闘終了時の entities クリアに依拠)。
 */
export const STATUS_EFFECT_DEFS = {
    'mark.physical': {
        id: 'mark.physical',
        element: 'physical',
        kind: 'stacking-debuff',
        durationTicks: 300,
        maxStacks: 5,
        description: '物理印。1 stack あたり 10s (300 ticks) 自然減衰、最大 5 stack。',
    },
    'mark.fire': {
        id: 'mark.fire',
        element: 'fire',
        kind: 'stacking-debuff',
        durationTicks: 240,
        maxStacks: 5,
        description: '火印。1 stack あたり 8s (240 ticks) 自然減衰、最大 5 stack。',
    },
    'mark.ice': {
        id: 'mark.ice',
        element: 'ice',
        kind: 'stacking-debuff',
        durationTicks: 420,
        maxStacks: 5,
        description: '氷印。1 stack あたり 14s (420 ticks) 自然減衰、最大 5 stack。',
    },
    'mark.lightning': {
        id: 'mark.lightning',
        element: 'lightning',
        kind: 'stacking-debuff',
        durationTicks: 180,
        maxStacks: 5,
        description: '雷印。1 stack あたり 6s (180 ticks) 自然減衰、最大 5 stack (高速回転)。',
    },
    'mark.earth': {
        id: 'mark.earth',
        element: 'earth',
        kind: 'stacking-debuff',
        durationTicks: 480,
        maxStacks: 4,
        description: '土印。1 stack あたり 16s (480 ticks) 自然減衰、最大 4 stack (持久戦)。',
    },
    'mark.light': {
        id: 'mark.light',
        element: 'light',
        kind: 'stacking-debuff',
        durationTicks: 360,
        maxStacks: 4,
        description: '光印。1 stack あたり 12s (360 ticks) 自然減衰、最大 4 stack。',
    },
    'mark.dark': {
        id: 'mark.dark',
        element: 'dark',
        kind: 'stacking-debuff',
        durationTicks: 420,
        maxStacks: 4,
        description: '闇印。1 stack あたり 14s (420 ticks) 自然減衰、最大 4 stack。',
    },
    kill_streak: {
        id: 'kill_streak',
        element: 'physical',
        kind: 'stacking-buff',
        durationTicks: 0,
        maxStacks: 7,
        description: 'キル連鎖カウンター。`onKill` で stacks +1、`onTakenDamage` で stacks 0。' +
            '戦闘中持続、戦闘終了で消滅 (durationTicks: 0 はダミー)。最大 7 stack。',
    },
};
//# sourceMappingURL=status.js.map